import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Button } from '@deal/components'
import useBreakpoint from '#src/hooks/useBreakpoint'
import { useExpertsForEmptyStateFacepileQuery } from './ExpertForEmptyStateFacepile.generated'
import { ExpertSearchSort } from '../../../generated/types'
import ExpertsFacepile from '../../ExpertsFacepile'
import step1Video from './clips/step-1.mp4'
import step2Video from './clips/step-2.mp4'
import step3Video from './clips/step-3.mp4'
import { generateCreateUrl } from '#src/services/path'
import styles from './styles.css'

interface StepProps {
  videoSrc: string
  step: string
}

/**
 * Should consider switching to something context based if this is needed on additional routes.
 * See: https://github.com/deal/consumer-app/pull/6037
 */
const getRouteSpecificCopy = (pathname: string) => {
  if (pathname.startsWith('/journal')) {
    return 'The Expert Journal brings you the best gear guides, travel tips, how-tos and more directly from Curated experts.'
  }

  return null
}

const Step: React.FC<StepProps> = ({ videoSrc, step }) => {
  return (
    <div className={styles.step}>
      <video
        autoPlay={true}
        muted
        playsInline
        controls={false}
        src={videoSrc}
        className={styles.video}
      />
      <span>{step}</span>
    </div>
  )
}

const EmptyState: React.FC = () => {
  const location = useLocation()
  const numberOfFaces = 9
  const { data } = useExpertsForEmptyStateFacepileQuery({
    variables: {
      query: {
        featuredOnly: true,
        facetedSearch: false
      },
      sortBy: ExpertSearchSort.PERFORMANCE,
      first: numberOfFaces
    }
  })
  const isLargerThanXxlg = useBreakpoint('xxlg')
  const routeSpecificCopy = getRouteSpecificCopy(location.pathname)

  return (
    <div className={styles.container}>
      <div className={styles.header}>Welcome to Curated</div>
      {routeSpecificCopy && <div className={styles.routeSpecificCopy}>{routeSpecificCopy}</div>}
      <div className={styles.subheader}>Shopping for gear? Here's how it works:</div>

      <div className={styles.border} />

      <Step videoSrc={step1Video} step="1. Take a 3-minute quiz" />
      <Step videoSrc={step2Video} step="2. Get expert recommendations" />
      <Step videoSrc={step3Video} step="3. Buy what's right for you" />

      <Link
        to={generateCreateUrl({
          sourceKey: 'chat-sidebar',
          pathIntent: 'request'
        })}
      >
        <Button className={styles.button}>Get started</Button>
      </Link>
      {data && (
        <div className={styles.bottomContainer}>
          <div className={styles.facepileContainer}>
            <ExpertsFacepile
              experts={data.expertSearchConnection.edges.map(edge => edge.node)}
              displayThreshold={numberOfFaces}
              avatarClassName={styles.avatar}
              avatarSize={isLargerThanXxlg ? 48 : 32}
            />
          </div>
          <div className={styles.facepileCopy}>350+ Active Experts</div>
        </div>
      )}
    </div>
  )
}

export default EmptyState
